import type { OBB, Vec2 } from '../../geo/types';
import type { Rng } from '../../core/rng';
import { obbCorners } from '../../geo/polygon';
import type { Lot, ReservedFootprint } from './types';
import { lotFrame } from './lotFrame';
import { lotKey } from './lotKey';
import { LOT } from './lotRules';
import type { Plot } from './plots';
import { BACK } from './backlots';

/**
 * PLAN Lots: a gang, a narrow alley, runs beside a house from its street to the back line of a deep
 * plot, and one-room dwellings (kamers) stand along it on the plot. The block between the Warmoesstraat,
 * the Oudezijds Armsteeg, the Oudezijds Voorburgwal and the Heintje Hoekssteeg, some 270 m round, was
 * full of back houses, workshops and one-room dwellings after 1400, reached by three gangen (Nikkels
 * 2013): one gang in about `spacing` metres of street front, where the inner city had many stegen and
 * few gangen (Wikipedia, "Gang (steeg)"). A gang was as wide as a porter with a basket of peat needed
 * (Erfgoed van de Week 2020): a 16th-century steeg between two lots on the Oude Turfmarkt was 1 m wide
 * (AAR 31), a passage in a back house on the Oudezijds Armsteeg of about 1615 0.6 m (AAR 60). A kamer
 * is about 4 m along the gang and fills the plot's width beside it: a back building of 6.50 by 4.10 m
 * stood on the same Armsteeg lots.
 */
export const GANG = {
  width: 1.0,
  spacing: 100,
  /** A courtyard and three kamers deep. */
  minPlot: 16,
  kamer: { min: 4, max: 4.5, least: 3.5 },
  /** What the house keeps of its front beside the gang: the least street frontage. */
  minHouse: 4,
} as const;

export interface Gang {
  readonly key: string;
  /** The house narrowed to let the gang pass. */
  readonly house: Lot;
  /** The gang's ground from the street front to the back line, its front edge first, kept free of buildings. */
  readonly strip: ReservedFootprint;
  /** The stretch of the gang behind the house, across the plot: the only part other buildings could reach. */
  readonly behind: OBB;
  readonly kamers: readonly Lot[];
}

/** The house without the gang's width on `side` (1 right, -1 left, seen from the street). */
function narrowed(house: Lot, side: number): Lot {
  const { right } = lotFrame(house);
  const shift = (side * GANG.width) / 2;
  const center: Vec2 = [
    house.obb.center[0] - right[0] * shift,
    house.obb.center[1] - right[1] * shift,
  ];
  const obb: OBB = { ...house.obb, center, halfU: (house.frontage - GANG.width) / 2 };
  return { ...house, obb, corners: obbCorners(obb), frontage: house.frontage - GANG.width };
}

/** A point `u` metres to the right of the front middle and `d` metres behind the front. */
function at(house: Lot, u: number, d: number): Vec2 {
  const { frontMid, facing, right } = lotFrame(house);
  return [frontMid[0] + right[0] * u - facing[0] * d, frontMid[1] + right[1] * u - facing[1] * d];
}

/*
 * The gang stands off the plot's side line and the narrowed house by the lots' overlap tolerance, so
 * buildings that touch those lines stay clear of it: an outline counts touching as crossing.
 */
function stripOf(house: Lot, side: number, length: number, key: string): ReservedFootprint {
  const inner = side * (house.frontage / 2 - GANG.width + LOT.overlapTolerance);
  const outer = side * (house.frontage / 2 - LOT.overlapTolerance);
  const ring = [
    at(house, inner, 0),
    at(house, outer, 0),
    at(house, outer, length),
    at(house, inner, length),
  ];
  return { id: key, polygon: { outer: ring, holes: [] } };
}

function behindOf(plot: Plot, side: number): OBB {
  const { depth } = lotFrame(plot.lot);
  return {
    center: at(plot.lot, side * (plot.lot.frontage / 2 - GANG.width / 2), depth + plot.depth / 2),
    axisU: plot.lot.obb.axisU,
    halfU: GANG.width / 2 - LOT.overlapTolerance,
    halfV: plot.depth / 2,
  };
}

function kamerAt(
  house: Lot,
  side: number,
  from: number,
  to: number,
  ordinal: number,
  key: string
): Lot {
  const { facing, right } = lotFrame(house);
  const across = house.frontage - GANG.width;
  const obb: OBB = {
    center: at(house, -side * (GANG.width / 2), (from + to) / 2),
    axisU: [-facing[0], -facing[1]],
    halfU: (to - from) / 2,
    halfV: across / 2,
  };
  return {
    index: -1,
    sourceId: key,
    side: side > 0 ? 'right' : 'left',
    ordinal,
    kind: 'backhouse',
    obb,
    corners: obbCorners(obb),
    frontage: to - from,
    depth: across,
    facing: [right[0] * side, right[1] * side],
  };
}

/** The kamers along the gang, from behind the courtyard to the back line, numbered from the street. */
function kamersOf(plot: Plot, side: number, rng: Rng, key: string): readonly Lot[] {
  const { depth } = lotFrame(plot.lot);
  const end = depth + plot.depth;
  const kamers: Lot[] = [];
  let from = depth + rng.fork('yard').range(BACK.courtyard.min, BACK.courtyard.max);
  for (let k = 0; end - from >= GANG.kamer.least; k++) {
    const to = Math.min(end, from + rng.fork(`k${k}`).range(GANG.kamer.min, GANG.kamer.max));
    kamers.push(kamerAt(plot.lot, side, from, to, k, key));
    from = to;
  }
  return kamers;
}

function gangOf(plot: Plot, own: Rng, key: string): Gang {
  const side = own.fork('side').chance(0.5) ? 1 : -1;
  const length = lotFrame(plot.lot).depth + plot.depth;
  return {
    key,
    house: narrowed(plot.lot, side),
    strip: stripOf(plot.lot, side, length, key),
    behind: behindOf(plot, side),
    kamers: kamersOf(plot, side, own, key),
  };
}

function eligible(plot: Plot): boolean {
  return (
    plot.lot.kind === 'house' &&
    plot.depth >= GANG.minPlot &&
    plot.lot.frontage - GANG.width >= GANG.minHouse
  );
}

/**
 * The gangen of the city: each house on a plot deep enough opens one with a chance of its frontage in
 * `spacing`, drawn from the house's own name, so a gang stays put whatever houses stand beside it.
 */
export function planGangen(plots: readonly Plot[], rng: Rng): readonly Gang[] {
  return plots.flatMap((plot) => {
    if (!eligible(plot)) return [];
    const key = `gang:${lotKey(plot.lot)}`;
    const own = rng.fork(key);
    return own.fork('open').chance(plot.lot.frontage / GANG.spacing)
      ? [gangOf(plot, own, key)]
      : [];
  });
}
